// PWA install affordance for the Splash screen.
//
// Chrome / Edge / Android / desktop-Chrome all fire `beforeinstallprompt`
// once the install criteria are met (manifest + SW + HTTPS + icons +
// some user-engagement heuristic). We stash the event and surface a
// tap-to-install button that calls `event.prompt()` from inside the
// click handler — must be a user gesture or the prompt is silently
// suppressed.
//
// iOS Safari has no install API at all; the user has to use the system
// share sheet. We detect iOS Safari and show concise step-by-step
// guidance instead of a button.
//
// Once the page is running as an installed PWA (display-mode:standalone
// or navigator.standalone on iOS), the whole component renders
// nothing.

import { useEffect, useState } from 'react'

interface BeforeInstallPromptEvent extends Event {
  readonly platforms: string[]
  prompt(): Promise<void>
  readonly userChoice: Promise<{ outcome: 'accepted' | 'dismissed'; platform: string }>
}

function isStandalone(): boolean {
  if (typeof window === 'undefined') return false
  if (window.matchMedia?.('(display-mode: standalone)').matches) return true
  // iOS Safari adds `standalone` to navigator when launched from the
  // home screen — Chrome doesn't, so the matchMedia check above
  // covers it.
  const nav = window.navigator as Navigator & { standalone?: boolean }
  return nav.standalone === true
}

function isIosSafari(): boolean {
  if (typeof window === 'undefined') return false
  const ua = window.navigator.userAgent
  const isIos = /iPhone|iPad|iPod/.test(ua)
  // Chrome on iOS contains "CriOS"; Firefox is "FxiOS"; Edge is "EdgiOS".
  // None of those can install — only mobile Safari can.
  const isSafari = /Safari/.test(ua) && !/CriOS|FxiOS|EdgiOS/.test(ua)
  return isIos && isSafari
}

const DISMISSED_KEY = 'multispeaker:install-dismissed'

export function InstallPrompt() {
  const [installEvent, setInstallEvent] = useState<BeforeInstallPromptEvent | null>(null)
  const [installed, setInstalled] = useState(isStandalone())
  const [dismissed, setDismissed] = useState(() => {
    try { return window.localStorage.getItem(DISMISSED_KEY) === '1' } catch { return false }
  })

  useEffect(() => {
    const onPrompt = (e: Event) => {
      e.preventDefault()
      setInstallEvent(e as BeforeInstallPromptEvent)
    }
    const onInstalled = () => {
      setInstalled(true)
      setInstallEvent(null)
    }
    window.addEventListener('beforeinstallprompt', onPrompt)
    window.addEventListener('appinstalled', onInstalled)
    return () => {
      window.removeEventListener('beforeinstallprompt', onPrompt)
      window.removeEventListener('appinstalled', onInstalled)
    }
  }, [])

  const dismiss = () => {
    setDismissed(true)
    try { window.localStorage.setItem(DISMISSED_KEY, '1') } catch { /* private mode */ }
  }

  const install = async () => {
    if (!installEvent) return
    try {
      await installEvent.prompt()
      const choice = await installEvent.userChoice
      if (choice.outcome === 'accepted') setInstalled(true)
      setInstallEvent(null)
    } catch {
      /* user cancelled or browser blocked */
    }
  }

  if (installed) return null
  if (dismissed) return null

  // Native install (Chrome/Edge/Android)
  if (installEvent) {
    return (
      <div className="mt-6 flex items-center justify-between gap-3 rounded-xl border border-zinc-800 bg-zinc-900/40 px-4 py-3">
        <div className="min-w-0">
          <div className="text-sm font-medium text-zinc-100">Install Multispeaker</div>
          <div className="text-xs text-zinc-500">
            One tap to add it as an app — launches offline, no browser chrome.
          </div>
        </div>
        <div className="flex shrink-0 gap-2">
          <button
            type="button"
            onClick={dismiss}
            className="rounded-md px-2 py-1 text-xs text-zinc-500 hover:text-zinc-300"
          >
            Not now
          </button>
          <button
            type="button"
            onClick={() => void install()}
            className="rounded-md bg-blue-500 px-3 py-1.5 text-sm font-medium text-white shadow-sm active:scale-95 transition"
          >
            Install
          </button>
        </div>
      </div>
    )
  }

  // iOS Safari — no API, just guidance.
  if (isIosSafari()) {
    return (
      <div className="mt-6 rounded-xl border border-zinc-800 bg-zinc-900/40 px-4 py-3">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="text-sm font-medium text-zinc-100">Add to Home Screen</div>
            <div className="mt-1 text-xs text-zinc-400">
              Tap the <span className="font-medium text-zinc-200">Share</span> icon
              {' '}in Safari's toolbar, then choose{' '}
              <span className="font-medium text-zinc-200">Add to Home Screen</span>.
              The app then launches like any other.
            </div>
          </div>
          <button
            type="button"
            onClick={dismiss}
            className="shrink-0 rounded-md px-2 py-1 text-xs text-zinc-500 hover:text-zinc-300"
          >
            Got it
          </button>
        </div>
      </div>
    )
  }

  return null
}
